// src/utils/pdfGenerator.js
// Rapport PDF d'un audit, à la marque de l'agence (plan Enterprise) ou de CyberGuard par défaut.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DPE_COLORS = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', E: '#ef4444', F: '#dc2626', G: '#991b1b' };
const DPE_LABELS = { A: 'Excellent', B: 'Très bon', C: 'Bon', D: 'Moyen', E: 'Insuffisant', F: 'Faible', G: 'Critique' };
const STATUS = {
  '✅': { label: 'OK', color: [22, 163, 74] },
  '❌': { label: 'Critique', color: [220, 38, 38] },
  '⚠️': { label: 'À corriger', color: [217, 119, 6] },
  'ℹ️': { label: 'Info', color: [113, 113, 122] },
  '🔒': { label: 'Option', color: [113, 113, 122] },
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  const n = parseInt(m ? m[1] : '3b82f6', 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Les polices standard du PDF ne savent pas afficher les emojis : on les retire.
const pdfText = (s) => String(s ?? '')
  .replace(/[^\x20-\xFF–—‘’“”…€Œœ]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const statusOf = (line) => {
  for (const [prefix, st] of Object.entries(STATUS)) if (line.startsWith(prefix)) return st;
  return STATUS['ℹ️'];
};

export function splitSections(details) {
  const sections = { security: [], dns: [], rgpd: [], deep: [] };
  let current = 'security';
  for (const line of details || []) {
    if (line.includes('Analyse DNS')) { current = 'dns'; continue; }
    if (line.includes('Audit RGPD')) { current = 'rgpd'; continue; }
    if (line.startsWith('── Analyse approfondie')) { current = 'deep'; continue; }
    if (line.startsWith('🔒 Analyse approfondie')) { sections.deep.push(line); continue; }
    sections[current].push(line);
  }
  return sections;
}

const logoFormat = (dataUrl) => (/^data:image\/jpe?g/.test(dataUrl) ? 'JPEG' : 'PNG');

function imageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function generateAuditPDF(audit, { report = null, branding = null, clientName = null } = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const brand = hexToRgb(branding?.primary_color);
  const company = pdfText(branding?.company_name) || 'CyberGuard';
  const M = 18;

  // ── En-tête
  doc.setFillColor(...brand);
  doc.rect(0, 0, W, 4, 'F');
  let headerBottom = 26;
  if (branding?.logo) {
    const size = await imageSize(branding.logo);
    if (size) {
      const maxW = 50, maxH = 16;
      const ratio = Math.min(maxW / size.w, maxH / size.h);
      const w = size.w * ratio, h = size.h * ratio;
      try { doc.addImage(branding.logo, logoFormat(branding.logo), M, 10, w, h); headerBottom = Math.max(headerBottom, 10 + h + 4); }
      catch { /* logo illisible : on garde le nom seul */ }
    }
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...brand);
    doc.text(company, M, 20);
  }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
  doc.text("Rapport d'audit de sécurité", W - M, 14, { align: 'right' });
  doc.text(`Établi le ${new Date().toLocaleDateString('fr-FR')}`, W - M, 19, { align: 'right' });
  if (branding?.logo && branding?.company_name) doc.text(company, W - M, 24, { align: 'right' });

  // ── Titre
  let y = headerBottom + 8;
  doc.setTextColor(20, 20, 20); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
  doc.text(pdfText(audit.target_domain), M, y);
  y += 7;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90, 90, 90);
  const sub = [clientName ? `Client : ${pdfText(clientName)}` : null, `Audit du ${new Date(audit.created_at).toLocaleDateString('fr-FR')}`].filter(Boolean).join('   ·   ');
  doc.text(sub, M, y);

  // ── Score
  y += 8;
  const grade = audit.dpe_grade || 'D';
  const gradeRgb = hexToRgb(DPE_COLORS[grade]);
  doc.setFillColor(246, 246, 247); doc.roundedRect(M, y, W - 2 * M, 30, 2, 2, 'F');
  doc.setFillColor(...gradeRgb); doc.roundedRect(M + 6, y + 5, 20, 20, 2, 2, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
  doc.text(grade, M + 16, y + 18.5, { align: 'center' });
  doc.setTextColor(20, 20, 20); doc.setFontSize(22);
  doc.text(`${audit.security_score ?? '--'}/100`, M + 34, y + 15);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(90, 90, 90);
  doc.text(`Score de sécurité — ${DPE_LABELS[grade] || ''}`, M + 34, y + 22);
  const v = audit.vulnerabilities || {};
  const counts = [['Critiques', v.critical || 0], ['Élevées', v.high || 0], ['Moyennes', v.medium || 0], ['Faibles', v.low || 0]];
  counts.forEach(([label, n], i) => {
    const x = W - M - 4 - (counts.length - i) * 22;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(20, 20, 20);
    doc.text(String(n), x + 11, y + 15, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(110, 110, 110);
    doc.text(label, x + 11, y + 21, { align: 'center' });
  });
  y += 38;

  // ── Résultats par section
  const sections = splitSections((audit.scan_details || []).filter((l) => !l.startsWith('🔒')));
  const blocks = [
    ['Sécurité du site', sections.security],
    ['Emails et DNS', sections.dns],
    ['Analyse approfondie', sections.deep],
    ['Conformité RGPD et mentions légales', sections.rgpd],
  ];
  for (const [title, lines] of blocks) {
    if (!lines.length) continue;
    autoTable(doc, {
      startY: y,
      head: [[{ content: title, colSpan: 2 }]],
      body: lines.map((l) => { const st = statusOf(l); return [st.label, pdfText(l)]; }),
      theme: 'grid',
      margin: { left: M, right: M },
      headStyles: { fillColor: brand, textColor: 255, fontStyle: 'bold', fontSize: 10 },
      styles: { fontSize: 8.5, cellPadding: 2.2, lineColor: [230, 230, 230], textColor: [40, 40, 40] },
      columnStyles: { 0: { cellWidth: 24, fontStyle: 'bold' } },
      didParseCell: (d) => {
        if (d.section === 'body' && d.column.index === 0) d.cell.styles.textColor = statusOf(lines[d.row.index]).color;
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Rapport IA (plan Pro et plus), s'il a été généré
  if (report) {
    doc.addPage();
    y = 20;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(...brand);
    doc.text('Analyse et plan de correction', M, y);
    y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
    const intro = [report.risk_level ? `Niveau de risque : ${pdfText(report.risk_level)}.` : '', pdfText(report.summary), pdfText(report.risk_comment)].filter(Boolean).join(' ');
    const lines = doc.splitTextToSize(intro, W - 2 * M);
    doc.text(lines, M, y);
    y += lines.length * 5 + 4;
    if (report.recommendations?.length) {
      autoTable(doc, {
        startY: y,
        head: [['#', 'Action', 'Pourquoi']],
        body: report.recommendations.map((r, i) => [r.priority || i + 1, pdfText(r.action), pdfText([r.detail, r.impact].filter(Boolean).join(' — '))]),
        theme: 'grid', margin: { left: M, right: M },
        headStyles: { fillColor: brand, textColor: 255 }, styles: { fontSize: 8.5, cellPadding: 2.2 },
        columnStyles: { 0: { cellWidth: 9 }, 1: { cellWidth: 55, fontStyle: 'bold' } },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    if (report.vulnerabilities_explained?.length) {
      autoTable(doc, {
        startY: y,
        head: [['Point', 'Comment corriger']],
        body: report.vulnerabilities_explained.map((x) => [pdfText(x.title), pdfText(x.correction_guide || x.simple_fix)]),
        theme: 'grid', margin: { left: M, right: M },
        headStyles: { fillColor: brand, textColor: 255 }, styles: { fontSize: 8.5, cellPadding: 2.2 },
        columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' } },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    if (report.action_plan?.length) {
      autoTable(doc, {
        startY: y,
        head: [['Étape', 'Échéance', 'Tâche']],
        body: report.action_plan.map((a, i) => [a.step || i + 1, pdfText(a.timeline), pdfText(a.task)]),
        theme: 'grid', margin: { left: M, right: M },
        headStyles: { fillColor: brand, textColor: 255 }, styles: { fontSize: 8.5, cellPadding: 2.2 },
        columnStyles: { 0: { cellWidth: 14 }, 1: { cellWidth: 30 } },
      });
    }
  }

  // ── Pied de page
  const contact = [company, pdfText(branding?.contact_email), pdfText(branding?.website)].filter(Boolean).join('  ·  ');
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(225, 225, 225); doc.line(M, H - 14, W - M, H - 14);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(130, 130, 130);
    doc.text(contact, M, H - 9);
    doc.text(`Page ${i}/${pages}`, W - M, H - 9, { align: 'right' });
  }
  return doc;
}

export async function downloadAuditPDF(audit, options = {}) {
  const doc = await generateAuditPDF(audit, options);
  const slug = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const prefix = slug(options.branding?.company_name) || 'cyberguard';
  doc.save(`${prefix}_audit_${slug(audit.target_domain)}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
